import { useContext, useEffect, useState } from "react";
import Autocomplete from "@mui/material/Autocomplete";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import FormControl from "@mui/material/FormControl";
import IconButton from "@mui/material/IconButton";
import MenuItem from "@mui/material/MenuItem";
import Select from "@mui/material/Select";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import AddCircleOutlineRoundedIcon from "@mui/icons-material/AddCircleOutlineRounded";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import DataContext from "../../../context/DataContext";
import { DocumentsTopNavbar } from "../../../components/documents/DocumentsTopNavbar";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import { PumpEntryForm, PumpEntryFormFieldsConfig } from "../../../components/documents/pump/PumpEntryForm";
import { PumpStatusBadge } from "../../../components/documents/pump/PumpStatusBadge";
import { DeleteEntryDialog } from "../../../components/documents/pump/DeleteEntryDialog";
import { formatCOIDate, formatCOIInteger } from "../../../components/documents/coi/coiFormatters";
import {
  ListPageLayout,
  ListColumn,
  ListFilterField,
} from "../../../components/shared/ListPageLayout";
import { useFetch } from "../../../hooks/useFetch";
import { usePaginator } from "../../../hooks/usePaginator";
import { useRequest } from "../../../hooks/useRequest";
import {
  ClientProps,
  PumpEntryProps,
  PumpEntryStatus,
  PumpProps,
} from "../../../interfaces/interfaces";

const STATUS_OPTIONS: { value: PumpEntryStatus; label: string }[] = [
  { value: "in-stock", label: "In stock" },
  { value: "reused", label: "Reused" },
];

interface PumpEntryResponse {
  success?: boolean;
  data: PumpEntryProps[];
}

export type PumpModuleColumn =
  | "qty"
  | "pump"
  | "well"
  | "client"
  | "installationDate"
  | "ticketNumber"
  | "pulledDate"
  | "runtime"
  | "status"
  | "notes";

const DEFAULT_COLUMNS: PumpModuleColumn[] = [
  "qty",
  "pump",
  "well",
  "client",
  "pulledDate",
  "status",
  "notes",
];

const COLUMN_LABELS: Record<PumpModuleColumn, string> = {
  qty: "Qty",
  pump: "Pump",
  well: "Well",
  client: "Client",
  installationDate: "Installation date",
  ticketNumber: "Ticket #",
  pulledDate: "Pulled date",
  runtime: "Runtime (days)",
  status: "Status",
  notes: "Notes",
};

const COLUMN_WIDTHS: Record<PumpModuleColumn, string> = {
  qty: "70px",
  pump: "1.1fr",
  well: "1fr",
  client: "1fr",
  installationDate: "120px",
  ticketNumber: "110px",
  pulledDate: "120px",
  runtime: "130px",
  status: "1.2fr",
  notes: "1.6fr",
};

const calculateRuntimeDays = (
  installationDate?: string | null,
  pulledDate?: string | null,
) => {
  if (!installationDate || !pulledDate) return null;
  const [iy, im, id] = installationDate.slice(0, 10).split("-").map(Number);
  const [py, pm, pd] = pulledDate.slice(0, 10).split("-").map(Number);
  if (!iy || !im || !id || !py || !pm || !pd) return null;
  const installedUtc = Date.UTC(iy, im - 1, id);
  const pulledUtc = Date.UTC(py, pm - 1, pd);
  return Math.max(0, Math.round((pulledUtc - installedUtc) / (24 * 60 * 60 * 1000)));
};

interface Props {
  apiPrefix: string;
  title: string;
  canManage: boolean;
  clientMode?: boolean;
  assignedClientOptions?: ClientProps[];
  assignedClientId?: number | null;
  onAssignedClientChange?: (clientId: number | null) => void;
  columns?: PumpModuleColumn[];
  enableDelete?: boolean;
  formFields?: PumpEntryFormFieldsConfig;
}

export const PumpModuleAdmin = ({
  apiPrefix,
  title,
  canManage,
  clientMode = false,
  assignedClientOptions = [],
  assignedClientId = null,
  onAssignedClientChange,
  columns = DEFAULT_COLUMNS,
  enableDelete = false,
  formFields,
}: Props) => {
  const visibleColumns = columns.filter((col) => col !== "client" || !clientMode);
  const showStatusColumn = visibleColumns.includes("status");
  const { paginationModel, setPaginationModel } = usePaginator(apiPrefix);
  const {
    clientOptions: globalClientOptions,
    wellOptions,
    fetchDataClient,
    fetchDataWells,
  } = useContext(DataContext);
  const { handleRequest } = useRequest();
  const clientOptions = clientMode ? assignedClientOptions : globalClientOptions;

  const [clientId, setClientId] = useState("");
  const [pumpId, setPumpId] = useState("");
  const [pumps, setPumps] = useState<PumpProps[]>([]);
  const [search, setSearch] = useState("");
  const [status, setStatus] = useState("");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [logOpen, setLogOpen] = useState(false);
  const [editingRow, setEditingRow] = useState<PumpEntryProps | null>(null);
  const [deletingRow, setDeletingRow] = useState<PumpEntryProps | null>(null);
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    if (!clientMode) fetchDataClient();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clientMode]);

  useEffect(() => {
    if (!clientMode || !assignedClientId) return;
    setClientId(String(assignedClientId));
    fetchDataWells(assignedClientId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [assignedClientId, clientMode]);

  useEffect(() => {
    if (!clientId) {
      setPumps([]);
      return;
    }
    let active = true;
    handleRequest({
      endpoint: `${apiPrefix}-pumps/client/${clientId}`,
      options: { method: "GET" },
    })
      .then((response) => {
        if (active) setPumps(response.data || []);
      })
      .catch(() => {
        if (active) setPumps([]);
      });
    return () => {
      active = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clientId, apiPrefix]);

  const fetchFilters = { clientId, pumpId, search, status, dateFrom, dateTo };
  const { data, isLoading, error, fetchData, pagination } =
    useFetch<PumpEntryResponse>(
      `${apiPrefix}-paginate`,
      paginationModel,
      undefined,
      undefined,
      fetchFilters,
    );
  const rows = data?.data || [];

  const chooseClient = (value: string) => {
    const nextClientId =
      clientMode && !value && assignedClientId ? String(assignedClientId) : value;
    setClientId(nextClientId);
    setPumpId("");
    if (clientMode) onAssignedClientChange?.(nextClientId ? Number(nextClientId) : null);
    if (nextClientId) fetchDataWells(Number(nextClientId));
    // Client lives in toolbarActions, not the `filters` array, so
    // ListPageLayout's own filterSignature effect won't reset the page for
    // it — reset explicitly here, same as ReportsAdmin's chooseClient.
    setPaginationModel({ ...paginationModel, page: 0 });
  };

  const updateRowStatus = async (row: PumpEntryProps, nextStatus: PumpEntryStatus) => {
    await handleRequest({
      endpoint: `${apiPrefix}/${row.id}/status`,
      options: { method: "PUT", body: JSON.stringify({ status: nextStatus }) },
    });
    fetchData();
  };

  const confirmDeleteRow = async () => {
    if (!deletingRow) return;
    setDeleting(true);
    try {
      await handleRequest({
        endpoint: `${apiPrefix}/${deletingRow.id}`,
        options: { method: "DELETE" },
      });
      setDeletingRow(null);
      fetchData();
    } finally {
      setDeleting(false);
    }
  };

  const buildColumn = (col: PumpModuleColumn): ListColumn<PumpEntryProps> => {
    switch (col) {
      case "qty":
        return {
          key: "qty",
          header: COLUMN_LABELS.qty,
          render: (row) => (
            <Typography variant="body2" fontWeight={750}>
              {formatCOIInteger(row.quantity)}
            </Typography>
          ),
          width: COLUMN_WIDTHS.qty,
        };
      case "pump":
        return {
          key: "pump",
          header: COLUMN_LABELS.pump,
          render: (row) => (
            <Typography variant="body2" fontWeight={750}>
              {row.pump?.pumpNumber || "—"}
            </Typography>
          ),
          width: COLUMN_WIDTHS.pump,
        };
      case "well":
        return {
          key: "well",
          header: COLUMN_LABELS.well,
          render: (row) => row.well?.name || "—",
          width: COLUMN_WIDTHS.well,
        };
      case "client":
        return {
          key: "client",
          header: COLUMN_LABELS.client,
          render: (row) => row.client?.name || "—",
          width: COLUMN_WIDTHS.client,
        };
      case "installationDate":
        return {
          key: "installationDate",
          header: COLUMN_LABELS.installationDate,
          render: (row) => formatCOIDate(row.installationDate),
          width: COLUMN_WIDTHS.installationDate,
        };
      case "ticketNumber":
        return {
          key: "ticketNumber",
          header: COLUMN_LABELS.ticketNumber,
          render: (row) => row.ticketNumber || "—",
          width: COLUMN_WIDTHS.ticketNumber,
        };
      case "pulledDate":
        return {
          key: "pulledDate",
          header: COLUMN_LABELS.pulledDate,
          render: (row) => formatCOIDate(row.pulledDate),
          width: COLUMN_WIDTHS.pulledDate,
        };
      case "runtime":
        return {
          key: "runtime",
          header: COLUMN_LABELS.runtime,
          render: (row) => {
            const runtimeDays = calculateRuntimeDays(row.installationDate, row.pulledDate);
            return runtimeDays === null ? "—" : formatCOIInteger(runtimeDays);
          },
          width: COLUMN_WIDTHS.runtime,
        };
      case "status":
        return {
          key: "status",
          header: COLUMN_LABELS.status,
          render: (row) => (
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <PumpStatusBadge status={row.status} />
              {canManage ? (
                <FormControl size="small" sx={{ minWidth: 108 }}>
                  <Select
                    value={row.status}
                    onChange={(event) =>
                      updateRowStatus(row, event.target.value as PumpEntryStatus)
                    }
                    sx={{ fontSize: 12 }}
                  >
                    {STATUS_OPTIONS.map((option) => (
                      <MenuItem key={option.value} value={option.value}>
                        {option.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              ) : null}
            </Box>
          ),
          width: COLUMN_WIDTHS.status,
        };
      case "notes":
        return {
          key: "notes",
          header: COLUMN_LABELS.notes,
          render: (row) => (
            <Typography variant="body2" color="text.secondary">
              {row.notes || "—"}
            </Typography>
          ),
          width: COLUMN_WIDTHS.notes,
        };
      default:
        return { key: col, header: COLUMN_LABELS[col], render: () => "—" };
    }
  };

  const listColumns: ListColumn<PumpEntryProps>[] = visibleColumns.map(buildColumn);

  // Status is only shown to internal users: clientMode users never manage
  // pump status here (matches ToolsRunAdmin/COIAdmin, which both hide their
  // own Status filter from clientMode users the same way).
  const filters: ListFilterField[] = [
    {
      kind: "autocomplete",
      key: "pumpId",
      label: "Pump",
      value: pumpId,
      onChange: setPumpId,
      options: pumps.map((pump) => ({ value: String(pump.id), label: pump.pumpNumber })),
      placeholder: clientId ? "Search pumps…" : "Select a client",
      disabled: !clientId,
    },
    ...(showStatusColumn && !clientMode
      ? [
          {
            kind: "select" as const,
            key: "status",
            label: "Status",
            value: status,
            onChange: setStatus,
            options: STATUS_OPTIONS.map((option) => ({ value: option.value, label: option.label })),
          },
        ]
      : []),
    { kind: "date", key: "dateFrom", label: "Pull from", value: dateFrom, onChange: setDateFrom },
    { kind: "date", key: "dateTo", label: "To date", value: dateTo, onChange: setDateTo },
  ];

  const list = (
    <ListPageLayout<PumpEntryProps>
      title={title}
      columns={listColumns}
      rows={rows}
      getRowId={(row) => row.id}
      rowsLoading={isLoading}
      isError={Boolean(error)}
      errorMessage={`${title} could not be loaded.`}
      emptyTitle="No pumps found"
      emptySubtitle={
        clientMode
          ? "Adjust the filters to find pulled pumps."
          : "Select a client, adjust filters, or log a new pump pull."
      }
      searchValue={search}
      onSearchChange={(value) => setSearch(value.trim())}
      searchPlaceholder="Search by pump number…"
      filters={filters}
      page={paginationModel.page}
      pageSize={paginationModel.pageSize}
      totalCount={Number(pagination.totalRecords || 0)}
      onPageChange={(page) => setPaginationModel({ ...paginationModel, page })}
      onPageSizeChange={(pageSize) => setPaginationModel({ page: 0, pageSize })}
      toolbarActions={
        <Box sx={{ display: "flex", alignItems: "flex-end", gap: "10px" }}>
          <Box sx={{ width: 220 }}>
            <Box component="label" sx={{ display: "block", mb: "5px", color: "#9CA1AB", fontSize: 10.5, fontWeight: 600, letterSpacing: ".6px", lineHeight: 1.2, textTransform: "uppercase" }}>
              Client
            </Box>
            <Autocomplete
              size="small"
              options={clientOptions}
              value={clientOptions.find((client) => String(client.id) === clientId) || null}
              getOptionLabel={(client) => client.name}
              isOptionEqualToValue={(option, value) => option.id === value.id}
              onChange={(_, client) => chooseClient(client ? String(client.id) : "")}
              disableClearable={clientMode && clientOptions.length === 1}
              disabled={clientMode && clientOptions.length === 1}
              autoHighlight
              openOnFocus
              sx={{
                "& .MuiOutlinedInput-root": {
                  minHeight: 38,
                  bgcolor: "#FAFAF8",
                  borderRadius: "9px",
                  fontSize: 13,
                  "& fieldset": { borderColor: "#E2E2DD" },
                  "&:hover fieldset": { borderColor: "#C9C7BE" },
                  "&.Mui-focused fieldset": { borderColor: "#ED8B00", borderWidth: "1px" },
                },
                "& .MuiInputBase-input": { py: "8px" },
              }}
              renderInput={(params) => <TextField {...params} placeholder="Search clients…" />}
            />
          </Box>
          {canManage ? (
            <Button
              variant="outlined"
              startIcon={<AddCircleOutlineRoundedIcon />}
              onClick={() => setLogOpen(true)}
              sx={{
                borderColor: "#E7E7E3",
                color: "#2A2F38",
                borderRadius: "9px",
                px: "15px",
                py: "8px",
                fontSize: 13,
                fontWeight: 600,
                textTransform: "none",
                "&:hover": { borderColor: "#ED8B00", color: "#ED8B00" },
              }}
            >
              Log Pulled Pump
            </Button>
          ) : null}
        </Box>
      }
      rowActions={
        canManage
          ? (row) => (
              <>
                <IconButton size="small" onClick={() => setEditingRow(row)}>
                  <EditOutlinedIcon fontSize="small" />
                </IconButton>
                {enableDelete ? (
                  <IconButton size="small" onClick={() => setDeletingRow(row)}>
                    <DeleteOutlineIcon fontSize="small" />
                  </IconButton>
                ) : null}
              </>
            )
          : undefined
      }
    />
  );

  return (
    <Box sx={{ minHeight: "100vh", bgcolor: "#F3F3F0", color: "#29313D" }}>
      <DocumentsTopNavbar title={title} />
      <Box sx={{ maxWidth: 1680, mx: "auto", p: { xs: 2, md: 3.5 } }}>{list}</Box>
      <PumpEntryForm
        open={logOpen}
        onClose={() => setLogOpen(false)}
        onSaved={fetchData}
        apiPrefix={apiPrefix}
        clientOptions={clientOptions}
        wellOptions={wellOptions}
        fetchDataWells={fetchDataWells}
        initialClientId={clientId ? Number(clientId) : null}
        lockedClientId={clientMode ? assignedClientId : null}
        fields={formFields}
      />
      <PumpEntryForm
        open={Boolean(editingRow)}
        onClose={() => setEditingRow(null)}
        onSaved={fetchData}
        apiPrefix={apiPrefix}
        clientOptions={clientOptions}
        wellOptions={wellOptions}
        fetchDataWells={fetchDataWells}
        editingRow={editingRow}
        fields={formFields}
      />
      <DeleteEntryDialog
        open={Boolean(deletingRow)}
        onClose={() => setDeletingRow(null)}
        onConfirm={confirmDeleteRow}
        confirming={deleting}
        title="Delete pump record"
        description="This will permanently remove this pump record. This action cannot be undone."
      />
    </Box>
  );
};
